import { Box, Grid, Typography } from "@mui/material";
import { Alert } from "@mui/lab";
import * as api from "api/Knowledge";
import BoxDrawer from "components/BoxDrawer";
import { RequestIndicator } from 'components/CallServer';
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { RequestWrapIndicator } from "components/CallServer/RequestWrapIndicator";
import IconCalendar from "components/Icons/IconCalendar";
import IconComment from "components/Icons/IconComment";
import IconUserApprover from "components/Icons/IconUserApprover";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldCounter from "components/TextFieldCounter";
import ActionStatusName from "components/Translations/ActionStatusName";
import ApprovalStatusName from "components/Translations/ApprovalStatusName";
import UserFullProfileLink from "components/UserFullProfileLink";
import UserProfileLink from "components/UserProfileLink";
import { Formaters } from "lib/Formarters";
import { ApprovalStatus, PublicationActionStatus } from "models/ApiEnums";
import { FeedItemDto, PublicationActionGetDto, PublicationUnblockDto } from 'models/ApiModels';
import * as React from 'react';
import { Trans, WithTranslation, withTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    data: PublicationActionGetDto;
    loading: boolean;
    updating: boolean;
    canWrite: boolean;
    text: string;
    lastError: any;
}

interface IProps {
    publicationId: string;
    onClose: (result: FeedItemDto) => void;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class PublicationUnblockDrawer extends React.PureComponent<Props, IState> {

    private isNew: boolean = true;
    private unblockDto: PublicationUnblockDto;

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            loading: true,
            updating: false,
            canWrite: false,
            lastError: null,
            text: ""
        };
    }

    ///
    private res(r: string, options?: any) {
        return this.props.t("Views.Timeline.Dialogs.PublicationUnblockDrawer." + r, options);
    }

    ///
    private loadData() {
        api.canUnblockPublication(this.props.publicationId)
            .then((dto) => {
                this.unblockDto = dto;
                if (dto.canUnblock) {
                    api.getPublicationUnblockAction(
                        this.props.publicationId,
                    )
                        .then((data) => {

                            this.isNew = data == null || data.actionStatus != PublicationActionStatus.Pending;

                            this.setState({
                                data: data,
                                text: this.isNew ? "" : data.message,
                                loading: false,
                                canWrite: true
                            });
                        })
                        .catch((error) => {
                            this.setState({ loading: false, lastError: error });
                        });
                }
                else {
                    this.setState({ loading: false, data: null, canWrite: false });
                }
            })
            .catch((error) => {
                this.setState({ loading: false, lastError: error });
            });
    }

    ///
    private handleAccept = (text: string) => {
        const { data } = this.state;
        const { onClose, publicationId } = this.props;

        if (text != undefined && text.length > 0) {

            this.setState({ updating: true });

            if (this.isNew) {
                api.postRequestUnblockPublication(publicationId, text)
                    .then((feedItem) => {
                        if (feedItem) this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.RequestUnblockPublicationSuccess"), "success");
                        onClose(feedItem);
                    })
                    .catch((error) => this.setState({ updating: false, lastError: error }));
            }
            else {
                api.updateRequestUnblockPublication(data.actionId, text)
                    .then((feedItem) => {
                        if (feedItem) this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.RequestUnblockPublicationChangeSuccess"), "success");
                        onClose(feedItem);
                    })
                    .catch((error) => this.setState({ updating: false, lastError: error }));
            }
        }
        else {
            onClose(null);
        }
    };

    ///
    private handleRemove = () => {
        const { onClose } = this.props;

        this.setState({ updating: true });

        api.deleteRequestUnblockPublication(this.state.data.actionId)
            .then((feedItem) => {
                if (feedItem) this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.RequestUnblockPublicationDeletedSuccess"), "success");
                onClose(feedItem);
            })
            .catch((error) => this.setState({ updating: false, lastError: error }));
    };

    ///
    private renderUnavailable() {
        return (
            <MuiContainer>
                <Alert severity="warning">
                    {this.res("Unavailable")}
                </Alert>
            </MuiContainer>
        );
    }

    private renderBlockerHeader() {
        if (this.unblockDto == null || this.unblockDto.blocker == null || this.unblockDto.blockedOn == null)
            return null;

        return (
            <MuiContainer>
                <Alert severity="info" icon={false}>
                    <IconUserApprover /> <b><UserFullProfileLink user={this.unblockDto.blocker} /></b>
                    <br />
                    <IconCalendar /> {Formaters.localeDate(this.unblockDto.blockedOn)}
                    {
                        this.unblockDto.blockMessage &&
                        <>
                            <br />
                            <IconComment /> <i>&quot;{this.unblockDto.blockMessage}&quot;</i>
                        </>
                    }
                </Alert>
            </MuiContainer>
        );
    }
    ///
    private renderMessage() {
        if (this.state.data == null)
            return null;

        const { actionStatus, owner, ownerNotes, ownerResultStatus, completedOn } = this.state.data;

        if (actionStatus == PublicationActionStatus.Pending)
            return null;

        return (
            <MuiContainer>
                <MuiPaper elevation={3} borderColor="warning" paperColor="warning">

                    <Typography component="p" variant="body2">
                        <Trans
                            i18nKey="Views.Timeline.Dialogs.PublicationUnblockDrawer.RequestText"
                            components={
                                [
                                    actionStatus == PublicationActionStatus.Canceled || actionStatus == PublicationActionStatus.Ignored
                                        ? <ActionStatusName actionStatus={actionStatus} bold />
                                        : <ApprovalStatusName approvalStatus={ownerResultStatus} bold />
                                ]}
                        />
                    </Typography>

                    {
                        (ownerResultStatus == ApprovalStatus.Approved || ownerResultStatus == ApprovalStatus.Rejected) &&
                        <Typography component="p" variant="body2">
                            <Trans
                                i18nKey="Views.Timeline.Dialogs.PublicationUnblockDrawer.ReviewedText"
                                values={{ completedOn: completedOn }}
                                components={
                                    [
                                        <UserProfileLink user={owner} />,
                                        <b></b>
                                    ]
                                }
                            />

                            {
                                ownerNotes != null &&
                                <Box mt={2}>
                                    <b>{this.props.t("Views.Timeline.Dialogs.PublicationUnblockDrawer.NotesText")}:</b>
                                    <br />
                                    <i>{ownerNotes}</i>
                                </Box>
                            }
                        </Typography>
                    }

                </MuiPaper >
            </MuiContainer>
        );
    }

    ///
    private renderInput() {
        const { text } = this.state;


        return (
            <MuiPaper elevation={0} borderColor="mutedDark" paperColor="mutedLight">

                <TextFieldCounter
                    autoFocus
                    fullWidth
                    multiline
                    placeholder={this.res("InputPlaceholder")}
                    rows={5}
                    maxLength={1500}
                    value={this.state.text}
                    onChange={
                        (text) => {
                            this.setState({ text });
                        }
                    }
                />

                <Grid container justifyContent="center" alignItems="center" spacing={1}>
                    <Grid item>
                        <MuiRegularButton
                            width="large"
                            disabled={text.length == 0}
                            color="success"
                            onClick={() => this.handleAccept(text)}
                        >
                            {this.res(this.isNew ? "ButtonCreate" : "ButtonUpdate")}
                        </MuiRegularButton>
                    </Grid>
                    {
                        !this.isNew &&
                        <Grid item>
                            <MuiRegularButton
                                width="large"
                                color="error"
                                onClick={() => this.handleRemove()}
                            >
                                <span>{this.props.t("Common.Buttons.DeleteText")}</span>
                            </MuiRegularButton>
                        </Grid>
                    }
                </Grid>
            </MuiPaper>
        );
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { onClose } = this.props;
        const { loading, updating, canWrite, lastError, data } = this.state;

        var subTitle = data?.actionStatus == PublicationActionStatus.Pending
            ? this.res("LastRequest", { lastRequest: data.requestedOn })
            : null;

        return (
            <BoxDrawer
                open
                title={this.res("BoxTitle")}
                subtitle={subTitle}
                onClose={() => onClose(null)}
            >
                <RequestIndicator open={loading} />

                {
                    lastError
                        ? <RequestErrorReport error={lastError} />
                        : !loading && !canWrite
                            ? (
                                <>
                                    {this.renderBlockerHeader()}
                                    {this.renderUnavailable()}
                                </>
                            )
                            : (
                                <RequestWrapIndicator type="post" enabled={updating}>
                                    {this.renderBlockerHeader()}
                                    {this.renderMessage()}
                                    {this.renderInput()}
                                </RequestWrapIndicator>
                            )
                }
            </BoxDrawer >
        );
    }
}

export default withSnackbar(withTranslation()(PublicationUnblockDrawer));